import { ComponentProps, ReactNode, useEffect, useState } from 'react'
import { useMediaQuery } from 'usehooks-ts'

import { cn } from '@/theme/utils'
import {
  UiDialog,
  UiDialogContent,
  UiDialogDescription,
  UiDialogFooter,
  UiDialogHeader,
  UiDialogTitle,
} from '@/ui/UiDialog'
import {
  UiDrawer,
  UiDrawerContent,
  UiDrawerDescription,
  UiDrawerFooter,
  UiDrawerHeader,
  UiDrawerTitle,
} from '@/ui/UiDrawer'
import { UiScrollArea } from '@/ui/UiScrollArea'

type Props = {
  open: boolean
  onOpenChange?: (open: boolean) => void

  title: string | ReactNode
  description?: string
  content: ReactNode
  footer?: ReactNode

  modalProps?: ComponentProps<typeof UiDialogContent>
  drawerProps?: ComponentProps<typeof UiDrawerContent>

  headerWrapperClassName?: string
  headerClassName?: string
  scrollAreaClassName?: string

  isMaxZIndex?: boolean
}

export default function ResponsiveModalDrawer({
  open,
  onOpenChange,

  title,
  description,
  content,
  footer,
  modalProps,
  drawerProps,

  headerWrapperClassName,
  headerClassName,
  scrollAreaClassName,

  isMaxZIndex,
}: Props) {
  const isMdUp = useMediaQuery('(min-width: 768px)')

  // Lock layout mode while modal is open to prevent unmount/mount on resize
  const [lockedIsMdUp, setLockedIsMdUp] = useState<boolean | null>(null)
  useEffect(() => {
    if (open) {
      if (lockedIsMdUp === null) setLockedIsMdUp(isMdUp)
    } else {
      setLockedIsMdUp(null)
    }
  }, [open, isMdUp, lockedIsMdUp])

  const effectiveIsMdUp = lockedIsMdUp ?? isMdUp

  if (effectiveIsMdUp) {
    return (
      <UiDialog open={open} onOpenChange={onOpenChange}>
        <UiDialogContent
          {...modalProps}
          className={cn(
            isMaxZIndex && 'z-9999',
            modalProps?.className,
            'border-gradient',
          )}
          overlayProps={{
            className: cn(isMaxZIndex && 'z-[9999]', 'backdrop-blur-sm'),
          }}
        >
          {headerWrapperClassName ? (
            <div className={cn(headerWrapperClassName)}>
              <UiDialogHeader className={cn(headerClassName)}>
                <UiDialogTitle>{title}</UiDialogTitle>
                {description && (
                  <UiDialogDescription>{description}</UiDialogDescription>
                )}
              </UiDialogHeader>
            </div>
          ) : (
            <UiDialogHeader className={cn(headerClassName)}>
              <UiDialogTitle>{title}</UiDialogTitle>
              {description && (
                <UiDialogDescription>{description}</UiDialogDescription>
              )}
            </UiDialogHeader>
          )}

          <UiScrollArea
            className={cn(
              'content max-h-[60dvh] overflow-auto',
              scrollAreaClassName,
            )}
          >
            {content}
          </UiScrollArea>
          {footer && <UiDialogFooter>{footer}</UiDialogFooter>}
        </UiDialogContent>
      </UiDialog>
    )
  }

  return (
    <UiDrawer open={open} onOpenChange={onOpenChange}>
      <UiDrawerContent
        {...drawerProps}
        className={cn(
          'max-h-[80dvh]',
          isMaxZIndex && 'z-9999',
          drawerProps?.className,
        )}
        overlayProps={{
          className: cn(isMaxZIndex && 'z-[9999]'),
        }}
      >
        {headerWrapperClassName ? (
          <div className={cn(headerWrapperClassName)}>
            <UiDrawerHeader
              className={cn(
                'flex items-center justify-between pt-0',
                headerClassName,
              )}
            >
              <UiDrawerTitle>{title}</UiDrawerTitle>
              {description && (
                <UiDrawerDescription>{description}</UiDrawerDescription>
              )}
            </UiDrawerHeader>
          </div>
        ) : (
          <UiDrawerHeader
            className={cn(
              'flex items-center justify-between pt-0',
              headerClassName,
            )}
          >
            <UiDrawerTitle>{title}</UiDrawerTitle>
            {description && (
              <UiDrawerDescription>{description}</UiDrawerDescription>
            )}
          </UiDrawerHeader>
        )}

        <UiScrollArea
          className={cn(
            'content max-h-[80dvh] overflow-auto p-4',
            scrollAreaClassName,
          )}
        >
          {content}
        </UiScrollArea>
        {footer && <UiDrawerFooter>{footer}</UiDrawerFooter>}
      </UiDrawerContent>
    </UiDrawer>
  )
}
